import React from 'react';
import { ThemeProvider, useTheme, Button, MenuTrigger, Menu, MenuItem, SubmenuTrigger, Text, Flex, SideNav, SideNavLink, SideNavGroup, SubNav, getSubNavLinkClassName, SubNavLinkContent, House, Settings, LoadingOrbit, Switcher, Warning, Dialog, DialogTrigger, DialogContent, DialogFooter, TextField, Plus, Buildings, Breadcrumbs, Breadcrumb, Avatar, SignOut, FlowArrow, Sidebar, ShieldCheck, DocsContextProvider, DocsContextMenuWrapper, type DocInfo } from '@chatsdet/ui';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import {
  BookOpenText,
  Camera,
  Gauge,
  Check,
  Code,
  Compass,
  Database,
  Desktop,
  FileText,
  Flask,
  GitBranch,
  GithubLogo,
  Cube,
  Lifebuoy,
  ListChecks,
  Moon,
  Sparkle,
  Stack,
  Sun,
  Ticket,
  Toolbox,
  TreeStructure,
  X,
} from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import { GuideProvider } from '@runcofactor/guide';
import { NotificationProvider, NotificationBell } from '../../features/notifications';
import { AnnouncementHost } from '../../features/announcements';
import { AssistantHost, type AssistantPromptSeed } from '../../features/assistant/AssistantHost';
import { AssistantProvider } from '../../features/assistant/AssistantContext';
import type { AssistantOnboardingId } from '@chatsdet/types';
import type { KickoffRequest } from '../../features/assistant/onboardingFlow';
import { LegacyChatTombstone } from '../../features/assistant/LegacyChatTombstone';
import type { Corner } from '../../features/assistant/cornerPin';
import { useNotifications } from '../../features/notifications/hooks/useNotifications';
import { LoginPage } from '../pages/LoginPage';
import { NameWorkspace } from '../pages/NameWorkspace';
import { MfaChallengePage } from '../pages/MfaChallengePage';
import { SignUpPage } from '../pages/SignUpPage';
import { VerifyEmailPage } from '../pages/VerifyEmailPage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/ResetPasswordPage';
import { DOCS_URL, parseTenantAppHost } from '../../config';
import { resolveOrgSwitchUrl } from '../../lib/organization-context';
import { BreadcrumbsProvider, useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { isExploratoryPath } from '../../features/exploratory-tests/flavors';
import { SubNavProvider, useSubNavContext } from '../../contexts/SubNavContext';
import { AuthProvider, useAuth, type CreateOrgInput, type User as AuthUser } from '../../contexts/AuthContext';
import { BRAND, useFavicon } from '../../brand';
import { useUserSettings } from '../../hooks/useUserSettings';
import { TrialBanner } from './TrialBanner';
import { OutageIndicator } from './OutageIndicator';
import { useIsNarrowViewport } from '../../hooks/useIsNarrowViewport';
import { useSupportStatus } from '../../hooks/useSupportChat';
import { SupportChatWidget } from '../pages/support/SupportChatWidget';
import { FeedbackContextMenuPlugins } from '../../features/feedback';
import { PROMPT_LIBRARY_NAV, isPromptLibraryPath } from '../../features/prompt-library';
import {
  formatSupportUnreadCount,
  getUnreadSupportReplyNotificationIds,
} from './supportUnread';

export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppShellContent>{children}</AppShellContent>
      </AuthProvider>
    </ThemeProvider>
  );
};

function AppShellContent({ children }: { children: React.ReactNode }) {
  const _router = useRouter();
  const routerState = useRouterState();
  const name = BRAND.name;
  useFavicon();
  const { settings, updateSettings } = useUserSettings();
  const {
    user,
    organizations,
    isLoading: meLoading,
    isAccountDeactivated,
    isWorkspaceNotFound,
    switchOrg,
    createOrg,
    setDefaultOrg,
    signOut,
    isSwitchingOrg,
    isCreatingOrg,
  } = useAuth();

  // Set Sentry user context when authenticated
  const currentOrgName = organizations?.find((org) => org.id === user?.orgId)?.name;
  React.useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email ?? undefined,
        username: user.name ?? undefined,
      });
      Sentry.setTag('user_id', user.id);
      Sentry.setTag('organization_id', user.orgId);
      if (currentOrgName) {
        Sentry.setTag('organization_name', currentOrgName);
      }
    } else {
      Sentry.setUser(null);
    }
  }, [user, currentOrgName]);

  const [createOrgDialogOpen, setCreateOrgDialogOpen] = React.useState(false);
  const [newOrgName, setNewOrgName] = React.useState('');

  // Show loading screen while checking authentication
  if (meLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Flex direction="column" align="center" gap="md">
          <LoadingOrbit size="lg" />
          <Text className="text-muted-foreground">Loading...</Text>
        </Flex>
      </div>
    );
  }

  // Show a friendly message for an unknown per-org tenant host, instead of
  // the raw 403/logged-out state (typo'd or stale <label>.app.<domain> URL).
  //
  // Above the auth-page returns on purpose. This hostname belongs to no
  // organization, so nothing on it can succeed — including `/login` and
  // `/signup`, which are exactly where a stale bookmark or the Account
  // Deactivated button sends people. Ordering this after them would render an
  // ordinary sign-in form that can only ever fail, which is the precise thing
  // this screen exists to replace.
  if (isWorkspaceNotFound) {
    const genericAppHost = parseTenantAppHost(window.location.hostname)?.rest;
    const genericAppUrl = genericAppHost
      ? `${window.location.protocol}//${genericAppHost}`
      : undefined;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md mx-auto text-center space-y-4 p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Buildings size={32} className="text-muted-foreground" />
          </div>
          <Text weight="bold" className="text-xl">Workspace Not Found</Text>
          <Text className="text-muted-foreground">
            There's no {name} workspace at this address. Double-check the link,
            or continue to {name} and switch to the right organization from there.
          </Text>
          {genericAppUrl && (
            <Button variant="outline" onPress={() => (window.location.href = genericAppUrl)}>
              Continue to {name}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Auth pages should always render directly to avoid being wrapped in dashboard
  // This prevents the flash of dashboard content during auth transitions
  const pathname = routerState.location.pathname;
  if (pathname === '/signup') {
    return <SignUpPage />;
  }
  if (pathname === '/verify-email') {
    return <VerifyEmailPage />;
  }
  if (pathname === '/forgot-password') {
    return <ForgotPasswordPage />;
  }
  if (pathname === '/reset-password') {
    return <ResetPasswordPage />;
  }
  if (pathname === '/login') {
    return <LoginPage />;
  }
  // The MFA challenge lands the user here with no session yet — only the
  // short-lived mfa_challenge cookie — so it must render before the auth
  // gate below, same as /login.
  if (pathname === '/login/mfa') {
    return <MfaChallengePage />;
  }

  // Invite page must render before the auth gate so InviteAccept can
  // persist the token and show the org-specific sign-in/sign-up UI
  if (pathname.startsWith('/invite/')) {
    return <>{children}</>;
  }

  // Public gist viewer is unauthenticated (gated by the public API route).
  if (pathname.startsWith('/u/g/')) {
    return <>{children}</>;
  }

  // Show friendly deactivation message
  if (isAccountDeactivated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md mx-auto text-center space-y-4 p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Warning size={32} className="text-muted-foreground" />
          </div>
          <Text weight="bold" className="text-xl">Account Deactivated</Text>
          <Text className="text-muted-foreground">
            Your account has been deactivated. If you believe this is a mistake
            or would like to reactivate your account, please contact your
            organization administrator.
          </Text>
          <Button variant="outline" onPress={() => window.location.href = '/login'}>
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  // /invite/* is handled above so invited users can still accept.
  if (!user.orgId && user.canCreateOrganization) {
    return <NameWorkspace />;
  }
  if (!user.orgId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md mx-auto text-center space-y-4 p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Buildings size={32} className="text-muted-foreground" />
          </div>
          <Text weight="bold" className="text-xl">No Organization</Text>
          <Text className="text-muted-foreground">
            You don't have access to an organization yet. If you were invited,
            please use the invitation link from your email. Otherwise, contact
            the {name} team if you'd like to explore the product — or reach out
            if you think something has gone wrong.
          </Text>
          <Button variant="outline" onPress={() => signOut()}>
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  const currentOrg = organizations?.find((org) => org.id === user?.orgId);

  return (
    <NotificationProvider>
      <BreadcrumbsProvider>
        <SubNavProvider>
          <AppShellLayout
            user={user}
            organizations={organizations}
            currentOrg={currentOrg}
            settings={settings}
            updateSettings={updateSettings}
            switchOrg={switchOrg}
            setDefaultOrg={setDefaultOrg}
            signOut={signOut}
            isSwitchingOrg={isSwitchingOrg}
            isCreatingOrg={isCreatingOrg}
            createOrgDialogOpen={createOrgDialogOpen}
            setCreateOrgDialogOpen={setCreateOrgDialogOpen}
            newOrgName={newOrgName}
            setNewOrgName={setNewOrgName}
            createOrg={createOrg}
          >
            {children}
          </AppShellLayout>
        </SubNavProvider>
      </BreadcrumbsProvider>
    </NotificationProvider>
  );
}
interface AppShellLayoutProps {
  user: AuthUser;
  organizations:
    | Array<{ id: string; name: string; appSubdomain: string | null; isDefault: boolean }>
    | undefined;
  currentOrg: { id: string; name: string } | undefined;
  settings: {
    sidebarCollapsed: boolean;
    docsPanelOpen: boolean;
    assistantPanelOpen: boolean;
    assistantCorner: Corner;
  };
  updateSettings: (settings: {
    sidebarCollapsed?: boolean;
    docsPanelOpen?: boolean;
    assistantPanelOpen?: boolean;
    assistantCorner?: Corner;
  }) => void;
  switchOrg: (orgId: string) => void;
  setDefaultOrg: (orgId: string) => void;
  signOut: () => void;
  isSwitchingOrg: boolean;
  isCreatingOrg: boolean;
  createOrgDialogOpen: boolean;
  setCreateOrgDialogOpen: (open: boolean) => void;
  newOrgName: string;
  setNewOrgName: (name: string) => void;
  createOrg: (input: CreateOrgInput) => void;
  children: React.ReactNode;
}

function AppShellLayout({
  user,
  organizations,
  settings,
  updateSettings,
  switchOrg,
  setDefaultOrg,
  signOut,
  isCreatingOrg,
  createOrgDialogOpen,
  setCreateOrgDialogOpen,
  newOrgName,
  setNewOrgName,
  createOrg,
  children,
}: AppShellLayoutProps) {
  const router = useRouter();
  const routerState = useRouterState();
  const { config: subNavConfig, isSubNavActive } = useSubNavContext();
  const isNarrow = useIsNarrowViewport();
  const { config: themeConfig, setMode } = useTheme();

  // Legacy chat surfaces are discoverable tombstones that point at the
  // page-aware global Assistant.
  const assistantPanelOpen = settings.assistantPanelOpen ?? false;
  const [assistantPromptSeed, setAssistantPromptSeed] =
    React.useState<AssistantPromptSeed | null>(null);

  // Docs panel state
  const docsPanelOpen = !assistantPanelOpen && (settings.docsPanelOpen ?? false);
  const { notifications, markRead } = useNotifications();

  // Support panel state
  const [supportPanelOpen, setSupportPanelOpen] = React.useState(false);
  const [initialSupportConversationId, setInitialSupportConversationId] = React.useState<
    string | undefined
  >(undefined);
  const supportStatusQuery = useSupportStatus();
  const isSupportEnabled = supportStatusQuery.data?.data.enabled ?? false;
  const unreadSupportReplyIds = React.useMemo(
    () => getUnreadSupportReplyNotificationIds(notifications),
    [notifications]
  );
  const unreadSupportReplyCount = unreadSupportReplyIds.length;

  React.useEffect(() => {
    if (!supportPanelOpen || unreadSupportReplyIds.length === 0) return;
    for (const id of unreadSupportReplyIds) {
      markRead(id);
    }
  }, [markRead, supportPanelOpen, unreadSupportReplyIds]);

  // Deep-link: `?support=<conversationId>` (from a support-reply email or
  // notification) opens the support widget to that conversation. Read the raw
  // URL (not validated router search) so it works regardless of the active
  // route, then strip the param so a refresh doesn't reopen it. Keyed on the
  // router location so it fires on fresh loads AND in-app navigations.
  const locationSearch = routerState.location.search;
  React.useEffect(() => {
    if (!isSupportEnabled) return;
    const conversationId = new URLSearchParams(window.location.search).get('support');
    if (!conversationId) return;
    setInitialSupportConversationId(conversationId);
    updateSettings({ docsPanelOpen: false, assistantPanelOpen: false });
    setSupportPanelOpen(true);
    router.navigate({
      to: routerState.location.pathname,
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        delete next.support;
        return next;
      },
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch, isSupportEnabled]);

  const handleToggleSupportPanel = React.useCallback(() => {
    if (supportPanelOpen) {
      setSupportPanelOpen(false);
    } else {
      // Close sibling panels first (mutual exclusion)
      updateSettings({ docsPanelOpen: false, assistantPanelOpen: false });
      // Then open support
      setSupportPanelOpen(true);
    }
  }, [supportPanelOpen, updateSettings]);

  const handleCloseSupportPanel = React.useCallback(() => {
    setSupportPanelOpen(false);
    // Clear the deep-link target so reopening lands on the conversation list,
    // and a future deep-link to the same conversation re-triggers the widget.
    setInitialSupportConversationId(undefined);
  }, []);

  const handleOpenAssistantPanel = React.useCallback(
    (prompt?: string) => {
      setSupportPanelOpen(false);
      if (prompt) {
        setAssistantPromptSeed({ text: prompt, nonce: Date.now() });
      }
      updateSettings({ docsPanelOpen: false, assistantPanelOpen: true });
    },
    [updateSettings]
  );

  // Onboarding kickoff request — a page hands the assistant a guided
  // onboarding to run (see `onboardingFlow.ts`). `nonce` distinguishes two
  // requests for the same onboarding id so `AssistantHost` re-fires even if
  // the previous one already finished.
  const [assistantOnboardingRequest, setAssistantOnboardingRequest] = React.useState<{
    id: AssistantOnboardingId;
    params: Record<string, string> | undefined;
    nonce: number;
  } | null>(null);

  const handleStartOnboarding = React.useCallback(
    (id: AssistantOnboardingId, params?: Record<string, string>) => {
      // Docs/support panels never coexist with the takeover; the ordinary
      // assistant panel is Host's own concern — its captive-takeover
      // choreography closes it itself (step 1 of the enter sequence) via the
      // `onClose` it's already given, whether or not it happened to be open.
      setSupportPanelOpen(false);
      updateSettings({ docsPanelOpen: false });
      setAssistantOnboardingRequest({ id, params, nonce: Date.now() });
    },
    [updateSettings]
  );

  // A page's request to open the ordinary assistant panel with a hidden
  // kickoff turn (e.g. "watch this live agent") — see `onboardingFlow.ts`'s
  // `KickoffRequest`. `nonce` re-fires the same request if it repeats.
  const [assistantKickoffRequest, setAssistantKickoffRequest] = React.useState<
    (KickoffRequest & { nonce: number }) | null
  >(null);

  const handleStartThreadWithKickoff = React.useCallback(
    (request: KickoffRequest) => {
      setSupportPanelOpen(false);
      setAssistantKickoffRequest({ ...request, nonce: Date.now() });
      updateSettings({ docsPanelOpen: false, assistantPanelOpen: true });
    },
    [updateSettings]
  );

  // A page's request to open the assistant panel on a FRESH thread and send
  // `text` as the first VISIBLE user turn (contrast `assistantKickoffRequest`
  // above, which sends a hidden marker turn). `nonce` re-fires on a repeat
  // press of the same prompt.
  const [assistantAskRequest, setAssistantAskRequest] = React.useState<{
    text: string;
    params: Record<string, string>;
    nonce: number;
  } | null>(null);

  const handleAskAssistant = React.useCallback(
    (text: string, params: Record<string, string>) => {
      setSupportPanelOpen(false);
      setAssistantAskRequest({ text, params, nonce: Date.now() });
      updateSettings({ docsPanelOpen: false, assistantPanelOpen: true });
    },
    [updateSettings]
  );

  const handleCloseAssistantPanel = React.useCallback(() => {
    updateSettings({ assistantPanelOpen: false });
  }, [updateSettings]);

  const handleToggleAssistantPanel = React.useCallback(() => {
    if (assistantPanelOpen) {
      updateSettings({ assistantPanelOpen: false });
    } else {
      handleOpenAssistantPanel();
    }
  }, [assistantPanelOpen, handleOpenAssistantPanel, updateSettings]);

  const handleOpenChat = React.useCallback(
    (docInfo: DocInfo) => {
      handleOpenAssistantPanel(`Explain "${docInfo.title}" `);
    },
    [handleOpenAssistantPanel]
  );

  const handleCloseDocsPanel = React.useCallback(() => {
    updateSettings({ docsPanelOpen: false });
  }, [updateSettings]);

  const handleToggleDocsPanel = React.useCallback(() => {
    if (docsPanelOpen) {
      updateSettings({ docsPanelOpen: false });
    } else {
      setSupportPanelOpen(false);
      updateSettings({ docsPanelOpen: true, assistantPanelOpen: false });
    }
  }, [docsPanelOpen, updateSettings]);

  // Force the icon rail when sub-nav is active, and on narrow viewports (the open
  // 280px sidebar would leave the page and top bar ~110px at phone width). The
  // persisted sidebarCollapsed preference is never written for the narrow case.
  const effectivelyCollapsed = settings.sidebarCollapsed || isSubNavActive || isNarrow;

  // Calculate left offset based on nav state. The sub-nav case matches the open
  // main nav (280px): the collapsed rail (`w-16`) plus SubNav (`w-[calc(280px-4rem)]`)
  // sum to 280px in every theme, so content doesn't shift between sub-nav and normal pages.
  const leftOffset = effectivelyCollapsed && !isSubNavActive ? 'ml-16' : 'ml-[280px]';

  // Calculate right offset when either panel is open (only one can be open at a time).
  // The unified assistant floats over content instead of pushing it — it never
  // contributes an offset. Docs panel: 400px, Support panel: 380px.
  const rightOffset = docsPanelOpen ? 'md:mr-[400px]' : supportPanelOpen ? 'md:mr-[380px]' : '';

  // When sub-nav is active, keep header padding consistent so breadcrumbs don't shift further left
  const headerPaddingClass = isSubNavActive ? 'pl-6' : 'pl-2';

  // Determine active sub-nav item based on current path
  const currentSubNavId = React.useMemo(() => {
    if (!subNavConfig) return undefined;

    if (subNavConfig.getActiveId) {
      return subNavConfig.getActiveId({
        pathname: routerState.location.pathname,
        search: routerState.location.search,
      });
    }

    const pathname = routerState.location.pathname;
    // Find the item whose path matches the current pathname
    const activeItem = subNavConfig.items.find((item) => {
      // Exact match or if the item is the base path and we're on that path
      if (item.to === pathname) return true;
      // For nested paths, check if pathname starts with item.to (but not if item.to is the basePath)
      if (item.to !== subNavConfig.basePath && pathname.startsWith(item.to + '/')) return true;
      return false;
    });
    // If no exact match found and we're under (not on) basePath, use first item
    const isOnBasePath = pathname === subNavConfig.basePath || pathname === subNavConfig.basePath + '/';
    if (!activeItem && !isOnBasePath && pathname.startsWith(subNavConfig.basePath)) {
      return subNavConfig.items[0]?.id;
    }
    return activeItem?.id;
  }, [subNavConfig, routerState.location.pathname, routerState.location.search]);

  const organizationItems = React.useMemo(
    () =>
      organizations?.map((org) => ({
        id: org.id,
        label: org.name,
      })) ?? [],
    [organizations]
  );
  const hasManyOrganizations = organizationItems.length > 10;
  const filterOrganizationsByPrefix = React.useCallback(
    (item: { label: string }, query: string) =>
      item.label.toLowerCase().startsWith(query.toLowerCase()),
    []
  );

  return (
    // Wraps the WHOLE layout (main content below AND AssistantHost, rendered
    // later in this tree) so both `useGuideSurface` (in pages) and `useGuide`
    // (in the assistant, which drives it) share one registry. Declarations
    <AssistantProvider
      openAssistant={handleOpenAssistantPanel}
      startOnboarding={handleStartOnboarding}
      startThreadWithKickoff={handleStartThreadWithKickoff}
      askAssistant={handleAskAssistant}
    >
    <GuideProvider enabled>
      <DocsContextProvider
        docsBaseUrl={DOCS_URL}
        onOpenChat={handleOpenChat}
        showMenuWithoutDocs={user?.isSuperadmin}
        renderMenu={(props) => <DocsContextMenuWrapper {...props} />}
      >
      {user?.isSuperadmin && <FeedbackContextMenuPlugins />}
      {/* Exactly viewport height (not min-) so the data-app-main div below is
          the scroll container — pages like the boards can then cap themselves
          with `flex-1 min-h-0` and scroll inside their own columns. */}
      <div className="flex-col flex h-dvh">
        {/* Fixed SideNav with org switcher header */}
        <SideNav
          position="fixed"
          isCollapsed={effectivelyCollapsed}
          header={(() => {
            const currentOrg = organizations?.find((o) => o.id === user?.orgId);
            const initial = (currentOrg?.name ?? '?').trim().charAt(0).toUpperCase() || '?';
            return (
              <Switcher
                value={user?.orgId}
                items={organizationItems}
                onSelect={(orgId) => {
                  if (orgId === user?.orgId) return;
                  const targetOrg = organizations?.find((o) => o.id === orgId);
                  // A tenant host pins the whole tab session to one org — the
                  // API 400s any other org id selected there — so leaving it
                  // must always be a host change in a new tab, never an
                  // in-place switch, whether or not the target org has its own
                  // subdomain. See resolveOrgSwitchUrl for the full decision
                  // table (it also covers the shared host and non-cofactor
                  // hosts like canary/localhost, which switch in place).
                  const switchUrl = resolveOrgSwitchUrl(
                    window.location.hostname,
                    window.location.protocol,
                    targetOrg?.appSubdomain ?? null,
                    orgId
                  );
                  if (switchUrl) {
                    // The target host has its own session cookie jar, separate
                    // from the current tab's. Navigating the current tab would
                    // destroy that session, so open the new host in a new tab
                    // instead — the user lands there unauthenticated (expected)
                    // and logs in fresh, keeping this tab's session intact.
                    window.open(switchUrl, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  switchOrg(orgId);
                }}
                actions={
                  user?.canCreateOrganization
                    ? [
                        {
                          id: 'create',
                          label: 'Create Organization',
                          icon: <Plus size={16} />,
                        },
                      ]
                    : undefined
                }
                onAction={(actionId) => {
                  if (actionId === 'create') {
                    setCreateOrgDialogOpen(true);
                  }
                }}
                placeholder="Select Organization"
                searchPlaceholder={
                  hasManyOrganizations ? 'Type an organization name...' : undefined
                }
                filter={hasManyOrganizations ? filterOrganizationsByPrefix : undefined}
                hideItemsUntilSearch={hasManyOrganizations}
                emptyText={
                  hasManyOrganizations
                    ? 'No organizations start with that search'
                    : undefined
                }
                triggerIcon={
                  <span
                    aria-hidden
                    data-rail-chip
                    className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-none border bg-transparent text-[#eef2f7] font-mono text-[12px] font-semibold flex-shrink-0"
                  >
                    {initial}
                  </span>
                }
                iconOnly={effectivelyCollapsed}
              />
            );
          })()}
          footer={
            <MenuTrigger>
              <Button
                variant="ghost"
                className={
                  effectivelyCollapsed
                    ? 'w-full hover:bg-transparent'
                    : // `p-0` is NOT dead: cofactor forced `padding-inline` only, so the
                      // block axis is live. Without it, md's `py-2` (7px at the 14px
                      // rem base) shrinks this fixed-40px button's content box to 26px
                      // around a ~34px avatar + two-line stack.
                      'w-full justify-start gap-3 p-0 hover:bg-transparent'
                }
              >
                <Avatar
                  src={user.picture}
                  name={user.name}
                  email={user.email}
                  size="sm"
                  className="avatar-hover-ring bg-[rgba(238,242,247,0.08)] text-[#eef2f7]"
                />
                {!effectivelyCollapsed && (
                  <div className="flex-1 min-w-0 text-left">
                    <Text size="sm" weight="medium" className="truncate block">
                      {user.name || user.email}
                    </Text>
                    {user.name && user.email && (
                      <Text size="xs" className="text-muted-foreground truncate block">
                        {user.email}
                      </Text>
                    )}
                  </div>
                )}
              </Button>
              <Menu
                className="min-w-[240px]"
                onAction={(key) => {
                  if (key === 'settings') {
                    router.navigate({ to: '/settings' });
                  } else if (key === 'superadmin') {
                    router.navigate({ to: '/superadmin' });
                  } else if (key === 'signout') {
                    signOut();
                  }
                }}
              >
                <MenuItem id="settings">
                  <Flex align="center" gap="sm">
                    <Settings size={16} className="text-muted-foreground" />
                    <span>Settings</span>
                  </Flex>
                </MenuItem>
                {user?.isSuperadmin && (
                  <MenuItem id="superadmin">
                    <Flex align="center" gap="sm">
                      <ShieldCheck size={16} className="text-muted-foreground" />
                      <span>Superadmin</span>
                    </Flex>
                  </MenuItem>
                )}
                {organizations && organizations.length > 1 && (
                  <SubmenuTrigger>
                    <Flex align="center" gap="sm">
                      <Buildings size={16} className="text-muted-foreground" />
                      <span>Default organization</span>
                    </Flex>
                    <Menu
                      className="min-w-[240px]"
                      onAction={(key) => {
                        const orgId = String(key);
                        if (orgId === '__default-org-help__') return;
                        const org = organizations.find((o) => o.id === orgId);
                        if (org && !org.isDefault) {
                          setDefaultOrg(orgId);
                        }
                      }}
                    >
                      <MenuItem id="__default-org-help__" isDisabled className="opacity-100">
                        <Text size="xs" className="text-muted-foreground text-pretty">
                          This is the org you land on after signing in.
                        </Text>
                      </MenuItem>
                      {organizations.map((org) => (
                        <MenuItem key={org.id} id={org.id}>
                          <Flex align="center" gap="sm" className="flex-1">
                            <span className="truncate">{org.name}</span>
                            {org.isDefault && <Check size={16} className="ml-auto flex-shrink-0" />}
                          </Flex>
                        </MenuItem>
                      ))}
                    </Menu>
                  </SubmenuTrigger>
                )}
                <SubmenuTrigger>
                  <Flex align="center" gap="sm">
                    {themeConfig.mode === 'system' ? (
                      <Desktop size={16} className="text-muted-foreground" />
                    ) : themeConfig.mode === 'light' ? (
                      <Sun size={16} className="text-muted-foreground" />
                    ) : (
                      <Moon size={16} className="text-muted-foreground" />
                    )}
                    <span>Theme</span>
                  </Flex>
                  <Menu
                    className="min-w-[160px]"
                    onAction={(key) => {
                      if (key === 'system') setMode('system');
                      else if (key === 'light') setMode('light');
                      else if (key === 'dark') setMode('dark');
                    }}
                  >
                    <MenuItem id="system">
                      <Flex align="center" gap="sm" className="flex-1">
                        <Desktop size={16} className="text-muted-foreground" />
                        <span>System</span>
                        {themeConfig.mode === 'system' && <Check size={16} className="ml-auto" />}
                      </Flex>
                    </MenuItem>
                    <MenuItem id="light">
                      <Flex align="center" gap="sm" className="flex-1">
                        <Sun size={16} className="text-muted-foreground" />
                        <span>Light</span>
                        {themeConfig.mode === 'light' && <Check size={16} className="ml-auto" />}
                      </Flex>
                    </MenuItem>
                    <MenuItem id="dark">
                      <Flex align="center" gap="sm" className="flex-1">
                        <Moon size={16} className="text-muted-foreground" />
                        <span>Dark</span>
                        {themeConfig.mode === 'dark' && <Check size={16} className="ml-auto" />}
                      </Flex>
                    </MenuItem>
                  </Menu>
                </SubmenuTrigger>
                <MenuItem id="signout">
                  <Flex align="center" gap="sm">
                    <SignOut size={16} className="text-muted-foreground" />
                    <span>Sign out</span>
                  </Flex>
                </MenuItem>
              </Menu>
            </MenuTrigger>
          }
        >
          <AppSideNav isCollapsed={effectivelyCollapsed} user={user} />
        </SideNav>

        {/* SubNav - renders when a page registers a sub-nav config */}
        {isSubNavActive && subNavConfig && (
          <SubNav
            title={subNavConfig.title}
            items={subNavConfig.items}
            activeId={currentSubNavId}
            searchable={subNavConfig.searchable}
            searchPlaceholder={subNavConfig.searchPlaceholder}
            renderItem={(item, isActive) => (
              <Link
                to={item.to}
                data-active={isActive ? '' : undefined}
                className={getSubNavLinkClassName(isActive)}
              >
                <SubNavLinkContent item={item} isActive={isActive} />
              </Link>
            )}
          />
        )}

        {/* Top Nav.
            NOTE: must be a plain <div>, not <Box>. Box defaults to margin='none'
            which injects `m-0` and can win the cascade over our `ml-[240px]`,
            collapsing the offset so the body slides under the fixed subnav. */}
        <div
          data-app-topbar
          className={`sticky top-0 z-50 bg-background/90 backdrop-blur border-b border-border h-16 transition-[margin] duration-300 ${leftOffset} ${rightOffset}`}
        >
          <Flex
            align="center"
            justify="between"
            className={`pr-6 ${headerPaddingClass} h-full`}
            gap="md"
          >
            <Flex align="center" gap="md" className="min-w-0 flex-1">
              {/* Hide sidebar toggle when sub-nav is active or the rail is forced (narrow viewport) */}
              {!isSubNavActive && !isNarrow && (
                <Button
                  variant="icon"
                  iconOnly
                  tooltip="Toggle sidebar"
                  onPress={() => updateSettings({ sidebarCollapsed: !settings.sidebarCollapsed })}
                >
                  <Sidebar size={20} />
                </Button>
              )}
              <AppBreadcrumbs />
            </Flex>

            <Flex gap="sm" align="center" className="shrink-0">
              <OutageIndicator />
              <NotificationBell />
              {user?.isSuperadmin && !user?.isInternalOrg && (
                <span data-org-pill>
                  <span data-dot />
                  <span className="sr-only sm:not-sr-only">External Org</span>
                </span>
              )}
              {isSupportEnabled && (
                <span className="relative inline-flex">
                  <Button variant="ghost" size="sm" onPress={handleToggleSupportPanel}>
                    <Lifebuoy size={18} className="sm:mr-2" />
                    <span className="sr-only sm:not-sr-only">Support</span>
                  </Button>
                  {unreadSupportReplyCount > 0 && (
                    <span
                      aria-label={`${unreadSupportReplyCount} unread support ${unreadSupportReplyCount === 1 ? 'reply' : 'replies'}`}
                      className="pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white dark:ring-neutral-900"
                    >
                      {formatSupportUnreadCount(unreadSupportReplyCount)}
                    </span>
                  )}
                </span>
              )}
              <Button variant="ghost" size="sm" onPress={handleToggleDocsPanel}>
                <BookOpenText size={18} className="sm:mr-2" />
                <span className="sr-only sm:not-sr-only">Docs</span>
              </Button>
            </Flex>
          </Flex>
        </div>

        {/* Trial banner — visible to all users when org is on trial.
            Plain <div> for the same reason as the topbar above. */}
        <div className={`transition-all duration-300 ${leftOffset} ${rightOffset}`}>
          <TrialBanner />
        </div>

        {/* Main content area offset by sidenav width.
            Plain <div> for the same reason as the topbar above.
            `flex flex-col` so a page can opt into filling the viewport height
            with a `flex-1 min-h-0` child (e.g. the boards). Pages that don't
            keep their natural content height and scroll as before. */}
        <div
          data-app-main
          className={`flex flex-col flex-1 overflow-y-auto transition-all duration-300 ${leftOffset} ${rightOffset}`}
        >
          {children}
        </div>

        {/* Create Organization Dialog */}
        <DialogTrigger isOpen={createOrgDialogOpen} onOpenChange={setCreateOrgDialogOpen}>
          <Button className="hidden">Trigger</Button>
          <Dialog
            title="Create Organization"
            description="Create a new organization to manage your team and resources."
          >
            <DialogContent>
              <TextField
                label="Organization Name"
                value={newOrgName}
                onChange={setNewOrgName}
                placeholder="Acme Inc."
              />
            </DialogContent>
            <DialogFooter>
              <Button variant="outline" onPress={() => setCreateOrgDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onPress={() => {
                  if (newOrgName.trim()) {
                    createOrg({ name: newOrgName.trim() });
                  }
                }}
                isDisabled={!newOrgName.trim() || isCreatingOrg}
              >
                {isCreatingOrg ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </Dialog>
        </DialogTrigger>

        {/* Feature-flag announcements (interstitial) */}
        <AnnouncementHost />

        {/* Unified assistant launcher and legacy docs tombstone live together. */}
        <AssistantHost
          isOpen={assistantPanelOpen}
          onClose={handleCloseAssistantPanel}
          onToggle={handleToggleAssistantPanel}
          onRequestOpen={handleOpenAssistantPanel}
          promptSeed={assistantPromptSeed}
          onboardingRequest={assistantOnboardingRequest}
          kickoffRequest={assistantKickoffRequest}
          askRequest={assistantAskRequest}
          corner={settings.assistantCorner}
          onCornerChange={(assistantCorner) => updateSettings({ assistantCorner })}
        />

        {docsPanelOpen ? (
          <div className="fixed right-0 top-0 bottom-0 z-40 flex w-[400px] flex-col border-l border-border bg-background shadow-xl">
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-4">
              <span className="text-sm font-medium text-foreground">Docs Assistant</span>
              <Button variant="icon" iconOnly tooltip="Close" onPress={handleCloseDocsPanel}>
                <X size={16} />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <LegacyChatTombstone
                className="h-full"
                title="Docs chat has moved"
                description="Use the page-aware Assistant to ask about documentation from anywhere."
              />
            </div>
          </div>
        ) : null}

        {/* Support Chat Widget */}
        {isSupportEnabled && (
          <SupportChatWidget
            isOpen={supportPanelOpen}
            onClose={handleCloseSupportPanel}
            initialConversationId={initialSupportConversationId}
          />
        )}
      </div>
      </DocsContextProvider>
    </GuideProvider>
    </AssistantProvider>
  );
}

function AppBreadcrumbs() {
  const { breadcrumbs } = useBreadcrumbs();

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs>
      {breadcrumbs.map((item, index) => {
        const key = item.key ?? index;

        if (item.node) {
          if (React.isValidElement(item.node)) {
            return React.cloneElement(item.node, { key });
          }

          return <Breadcrumb key={key}>{item.node}</Breadcrumb>;
        }

        if (!item.label) {
          return null;
        }

        return (
          <Breadcrumb key={key} onPress={item.onPress} href={item.href}>
            {item.label}
          </Breadcrumb>
        );
      })}
    </Breadcrumbs>
  );
}

/**
 * Sidebar "Labs" section: everything outside the core structure. Each item
 * shows only when one of its flags is on; the section is hidden when none are.
 */
const LABS_NAV_ITEMS = [
  { path: '/runs/qa', label: 'QA Runs', icon: Flask, flags: ['qaRuns'] },
  { path: '/releases', label: 'Releases', icon: GitBranch, flags: ['releaseQa', 'sandboxV2'] },
  { path: '/pr-reviews', label: 'PR Reviews', icon: GithubLogo, flags: ['prReview'] },
  { path: '/tickets', label: 'Tickets', icon: Ticket, flags: ['tickets'] },
  { path: '/boards/tasks', label: 'Tasks', icon: ListChecks, flags: ['agentTasks'] },
  { path: '/utilities', label: 'Utilities', icon: Toolbox, flags: ['utilities'] },
  // Pages and API Catalog have no feature of their own; `labs.data-catalog` gates
  // only their sidebar entries, not the routes.
  { path: '/pages', label: 'Pages', icon: FileText, flags: ['labsDataCatalog'] },
  { path: '/api-requests', label: 'API Catalog', icon: Code, flags: ['labsDataCatalog'] },
] as const;
type LabsNavItem = (typeof LABS_NAV_ITEMS)[number];

type SideNavSelection =
  | '/'
  | '/flows'
  | '/product-map'
  | '/context-library'
  | typeof PROMPT_LIBRARY_NAV.path
  | '/test-data'
  | '/issues'
  | '/suites'
  | '/exploratory'
  | '/sandboxes'
  | '/visual'
  | '/performance'
  | LabsNavItem['path'];

function AppSideNav({ isCollapsed, user }: { isCollapsed: boolean; user: AuthUser | null }) {
  const router = useRouter();
  const location = useRouterState({ select: (s) => s.location });
  const pathname = location.pathname;
  let selected: SideNavSelection | null = null;
  if (pathname === '/') selected = '/';
  else if (pathname.startsWith('/context-library')) selected = '/context-library';
  else if (isPromptLibraryPath(pathname)) selected = PROMPT_LIBRARY_NAV.path;
  else if (pathname.startsWith('/flows')) selected = '/flows';
  else if (pathname.startsWith('/product-map')) selected = '/product-map';
  else if (pathname.startsWith('/test-data')) selected = '/test-data';
  else if (pathname.startsWith('/issues')) selected = '/issues';
  // Test Runs live under /runs/test but belong to the Suites section, and so do
  // external run details: they are rows in that very same /runs/test table, so
  // opening one must not swap the highlight. `RunsLayout` already groups
  // /runs/external with Suites.
  else if (
    pathname.startsWith('/suites') ||
    pathname.startsWith('/runs/test') ||
    pathname.startsWith('/runs/external')
  )
    selected = '/suites';
  else if (isExploratoryPath(pathname)) selected = '/exploratory';
  else if (pathname.startsWith('/sandboxes')) selected = '/sandboxes';
  else if (pathname.startsWith('/visual')) selected = '/visual';
  else if (pathname.startsWith('/performance')) selected = '/performance';
  else {
    selected =
      LABS_NAV_ITEMS.find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))
        ?.path ?? null;
  }

  // Suites entry is always visible and lands on the GA Test Runs view (recent
  // results). The suite list/builder lives behind the "Configuration" tab at
  // /suites (gated by suite-builder.v2), reachable from the section sub-nav.
  const suitesHome = '/runs/test';

  const labsItems = LABS_NAV_ITEMS.filter((item) => item.flags.some((flag) => user?.features?.[flag]));

  return (
    <>
      {/* Overview Section */}
      <SideNavGroup title="Overview" isCollapsed={isCollapsed}>
        <SideNavLink
          href="/"
          isActive={selected === '/'}
          icon={<House size={18} />}
          isCollapsed={isCollapsed}
          onClick={(e) => {
            e.preventDefault();
            router.navigate({ to: '/' });
          }}
        >
          Home
        </SideNavLink>
        <SideNavLink
          href="/issues"
          isActive={selected === '/issues'}
          icon={<Warning size={18} />}
          isCollapsed={isCollapsed}
          onClick={(e) => {
            e.preventDefault();
            router.navigate({ to: '/issues' });
          }}
        >
          Issues
        </SideNavLink>
        {/* Always visible: Test Runs (/runs/test) is GA. The suites list and
            builder stay behind suite-builder.v2, so unflagged orgs land on
            Test Runs instead of /suites. */}
        <SideNavLink
          href={suitesHome}
          isActive={selected === '/suites'}
          icon={<Stack size={18} />}
          isCollapsed={isCollapsed}
          onClick={(e) => {
            e.preventDefault();
            router.navigate({ to: suitesHome });
          }}
        >
          Suites
        </SideNavLink>
        {user?.features?.sandboxV2 && (
          <SideNavLink
            href="/visual"
            isActive={selected === '/visual'}
            icon={<Camera size={18} />}
            isCollapsed={isCollapsed}
            onClick={(e) => {
              e.preventDefault();
              router.navigate({ to: '/visual', search: { status: 'changed' } });
            }}
          >
            Visual
          </SideNavLink>
        )}
        {user?.features?.sandboxV2 && (
          <SideNavLink
            href="/performance"
            isActive={selected === '/performance'}
            icon={<Gauge size={18} />}
            isCollapsed={isCollapsed}
            onClick={(e) => {
              e.preventDefault();
              router.navigate({ to: '/performance' });
            }}
          >
            Performance
          </SideNavLink>
        )}
      </SideNavGroup>

      {/* Test Studio Section */}
      <SideNavGroup title="Test Studio" isCollapsed={isCollapsed}>
        <SideNavLink
          href="/product-map"
          isActive={selected === '/product-map'}
          icon={<TreeStructure size={18} />}
          isCollapsed={isCollapsed}
          onClick={(e) => {
            e.preventDefault();
            router.navigate({ to: '/product-map' });
          }}
        >
          Product Map
        </SideNavLink>
        <SideNavLink
          href="/flows"
          isActive={selected === '/flows'}
          icon={<FlowArrow size={18} />}
          isCollapsed={isCollapsed}
          onClick={(e) => {
            e.preventDefault();
            router.navigate({ to: '/flows' });
          }}
        >
          Flows
        </SideNavLink>
        {user?.features?.adhocTester && (
          <SideNavLink
            href="/exploratory"
            isActive={selected === '/exploratory'}
            icon={<Compass size={18} />}
            isCollapsed={isCollapsed}
            onClick={(e) => {
              e.preventDefault();
              router.navigate({ to: '/exploratory' });
            }}
          >
            Exploratory
          </SideNavLink>
        )}
      </SideNavGroup>

      {/* Environments Section */}
      <SideNavGroup title="Environments" isCollapsed={isCollapsed}>
        {(user?.features?.environments || user?.features?.sandboxV2) && (
          <SideNavLink
            href="/sandboxes"
            isActive={selected === '/sandboxes'}
            icon={<Cube size={18} />}
            isCollapsed={isCollapsed}
            onClick={(e) => {
              e.preventDefault();
              router.navigate({ to: '/sandboxes' });
            }}
          >
            Test Environments
          </SideNavLink>
        )}
        <SideNavLink
          href="/test-data"
          isActive={selected === '/test-data'}
          icon={<Database size={18} />}
          isCollapsed={isCollapsed}
          onClick={(e) => {
            e.preventDefault();
            router.navigate({ to: '/test-data' });
          }}
        >
          Test Data
        </SideNavLink>
      </SideNavGroup>

      {/* Agent tools Section — always rendered: Context Library is ungated. */}
      <SideNavGroup title="Agent tools" isCollapsed={isCollapsed}>
        <SideNavLink
          href="/context-library"
          isActive={selected === '/context-library'}
          icon={<BookOpenText size={18} />}
          isCollapsed={isCollapsed}
          onClick={(e) => {
            e.preventDefault();
            router.navigate({ to: '/context-library' });
          }}
        >
          Context Library
        </SideNavLink>
        <SideNavLink
          href={PROMPT_LIBRARY_NAV.path}
          isActive={selected === PROMPT_LIBRARY_NAV.path}
          icon={<Sparkle size={18} />}
          isCollapsed={isCollapsed}
          onClick={(e) => {
            e.preventDefault();
            router.navigate({ to: PROMPT_LIBRARY_NAV.path });
          }}
        >
          {PROMPT_LIBRARY_NAV.label}
        </SideNavLink>
      </SideNavGroup>

      {/* Labs Section — bottom; hidden unless at least one item's flag is on. */}
      {labsItems.length > 0 && (
        <SideNavGroup title="Labs" isCollapsed={isCollapsed}>
          {labsItems.map(({ path, label, icon: Icon }) => (
            <SideNavLink
              key={path}
              href={path}
              isActive={selected === path}
              icon={<Icon size={18} />}
              isCollapsed={isCollapsed}
              onClick={(e) => {
                e.preventDefault();
                router.navigate({ to: path });
              }}
            >
              {label}
            </SideNavLink>
          ))}
        </SideNavGroup>
      )}
    </>
  );
}
